<template>
  <div class="tournamentLeaderboard">
    <table class="table" style="width: 100%; margin: auto">
      <thead>
        <tr>
          <th style="width: 15%">Rank</th>
          <th style="width: 50%; text-align: left">Player</th>
          <th style="width: 20%">Participation</th>
          <th style="width: 15%">Score</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="player in rankedPlayers"
          :key="player.id"
          :class="{
            'top-border': player.rankRange,
            even: player.rankGroup % 2,
          }"
        >
          <td style="width: 15%">
            {{ player.rankRange }}
            <Trophy
              v-if="
                player.reward &&
                standingIndex === totalRounds &&
                player.rankRange
              "
              :text="player.reward"
            />
          </td>
          <td style="width: 50%">
            <ProfileCard
              :player="player.user"
              type="long"
              color="white"
              :onlinePlayer="onlinePlayer(player.user.user_id)"
            />
          </td>
          <td style="width: 20%">
            <RoundResults
              :roundsParticipated="player.roundsParticipated"
              :currentRoundNumber="standingIndex"
              :totalRounds="totalRounds"
              :username="player.username"
              :games="player.games"
            />
          </td>
          <td style="width: 15%">{{ player.score }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import ProfileCard from "@/components/players/ProfileCard.vue";
import Trophy from "@/components/stats/Trophy.vue";
import RoundResults from "@/components/tournaments/RoundResults.vue";
import { getPlayerReward } from "vue-chess-shared/tournament/helper";
import { REWARD } from "../../constants";

export default {
  components: {
    ProfileCard,
    Trophy,
    RoundResults,
  },

  name: "TournamentLeaderboard",

  props: [
    "tournament",
    "rounds",
    "players",
    "games",
    "onlinePlayers",
    "standingIndex",
  ],

  computed: {
    totalRounds() {
      if (!this.tournament) return null;
      return this.tournament.totalRounds;
    },

    currentRoundNumber() {
      if (!this.tournament) return null;
      return this.tournament.currentRound;
    },

    rankedPlayers() {
      if (!this.players || !this.players.length) return [];

      // Seed leaderboard - round 0
      if (!this.rounds || !this.rounds.length || this.standingIndex === 0) {
        return this.players
          .map((p) => ({
            ...p,
            score: "-",
            seed: p.user.stats ? p.user.stats.rating : 0,
          }))
          .sort((a, b) => b.seed - a.seed)
          .map((p, i) => ({ ...p, rankRange: i + 1, rankGroup: i + 1 }));
      }

      let sortedPlayers;
      if (this.standingIndex === this.currentRoundNumber) {
        sortedPlayers = this.players.map((p) => ({ ...p, rank: 1 }));
      } else {
        let standings = this.rounds[this.standingIndex - 1].standingsAfter;
        if (standings) {
          sortedPlayers = this.players.map((p) => {
            let standing = standings.find(
              (s) => (s.player?.id ?? s.id) === p.username
            );
            return {
              ...p,
              score: standing ? standing.wins : 0,
              tiebreaker: standing ? standing.tiebreaker : 0,
              seed: standing ? standing.player.value ?? standing.seed : 0,
              matches: standing?.matches ?? 0,
            };
          });

          if (this.standingIndex >= this.tournament.totalRounds) {
            sortedPlayers = sortedPlayers.filter((p) => p.matches > 0);
          }
        } else
          sortedPlayers = this.players.map((p) => ({
            ...p,
            score: Math.max(
              0,
              ...this.games.map((g) =>
                g.roundNumber > this.standingIndex
                  ? 0
                  : g.whiteUsername === p.username
                  ? g.whiteScoreBeforeGame
                  : g.blackUsername === p.username
                  ? g.blackScoreBeforeGame
                  : 0
              )
            ),
          }));
      }

      let rank = 0;
      let rankGroupCount = 1;
      let rankGroup = 0;
      let lastP = null;
      sortedPlayers
        .sort(
          (a, b) =>
            b.score - a.score || b.tiebreaker - a.tiebreaker || b.seed - a.seed
        )
        .forEach((player, i) => {
          if (!player.roundsParticipated && lastP && lastP.roundsParticipated) {
            lastP = player;
            player.rankRange = "-";
            ++rankGroup;
          } else if (!lastP || player.score < lastP.score) {
            lastP = player;
            rank = i + 1;
            rankGroupCount = sortedPlayers.filter(
              (p) => p.score === player.score && p.roundsParticipated
            ).length;
            player.rankRange =
              rankGroupCount === 1
                ? rank
                : `${rank} - ${rank + rankGroupCount - 1}`;
            ++rankGroup;
          }
          player.rank = rank;
          player.rankGroup = rankGroup;
          player.games = this.games.filter(
            (g) =>
              g.whiteUsername === player.username ||
              g.blackUsername === player.username
          );
          // set reward for old tournaments
          if (Number.isInteger(this.tournament.tournamentId)) {
            player.reward = getPlayerReward(
              player.rank,
              this.tournament.totalRounds > 5 ? REWARD.TROPHY : REWARD.MEDAL,
              player.roundsParticipated
            );
          }
        });
      return sortedPlayers;
    },
  },

  methods: {
    onlinePlayer(userId) {
      if (!this.onlinePlayers) return null;
      return this.onlinePlayers.find((p) => p.user_id === userId);
    },
  },
};
</script>

<style scoped>
table tbody {
  display: block;
  height: 80vh;
  overflow-y: scroll;
}

table thead,
table tbody tr {
  display: table;
  width: 100%;
  table-layout: fixed;
}

.top-border {
  border-top: 1px solid #e7e7e7;
}

th {
  background-color: #5d095d;
  color: white !important;
  vertical-align: middle !important;
  height: 3rem;
}

tr {
  background-color: #573e97;
  color: white;
}

.even {
  background-color: #740074;
  color: white;
}

td {
  border: none !important;
}

th {
  border: none !important;
}
</style>
